'use client';

import { Clapperboard, Drama, Layers3, MicVocal, PersonStanding } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { FeatureTag } from '@/app/components/shared/FeatureTag';
import { useDevice } from '@/app/hooks';
import type { OneClickMvScene, OneClickMvVersion } from '@/app/types';
import { cn } from '@/app/utils';
import { ToolMediaUploadHeader } from '../../_components/ToolMediaUploadHeader';
import { ToolSettingsRadioGroup } from '../../_components/ToolSettingsRadioGroup';

interface OneClickMvModeSelectorProps {
  version: OneClickMvVersion;
  scene: OneClickMvScene;
  onVersionChange: (version: OneClickMvVersion) => void;
  onSceneChange: (scene: OneClickMvScene) => void;
}

const SCENE_OPTIONS = [
  { value: 'sing', icon: MicVocal },
  { value: 'sing_act', icon: Clapperboard },
  { value: 'dance', icon: PersonStanding },
  { value: 'performance', icon: Drama },
] as const;

export const OneClickMvModeSelector: FC<OneClickMvModeSelectorProps> = ({
  version,
  scene,
  onVersionChange,
  onSceneChange,
}) => {
  const t = useTranslations();
  const { supportsHover } = useDevice();

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <ToolMediaUploadHeader
          icon={<Layers3 className="size-5 text-system-white" />}
          title={t('tools.oneClickMv.version.label')}
        />
        <ToolSettingsRadioGroup
          itemSize="default"
          value={version}
          options={[
            { value: 'v1', label: t('tools.oneClickMv.version.v1') },
            {
              value: 'v2',
              label: (
                <span className="inline-flex items-center gap-2">
                  {t('tools.oneClickMv.version.v2')}
                  <FeatureTag text={t('tools.oneClickMv.version.new')} className="bg-[#5765F2] text-white" />
                </span>
              ),
            },
          ]}
          onValueChange={(value) => onVersionChange(value as OneClickMvVersion)}
        />
      </div>

      {version === 'v2' ? (
        <div className="space-y-3">
          <ToolMediaUploadHeader
            icon={<Clapperboard className="size-5 text-system-white" />}
            title={t('tools.oneClickMv.scene.label')}
          />
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-2">
            {SCENE_OPTIONS.map(({ value, icon: Icon }) => {
              const isActive = scene === value;
              return (
                <CustomTooltip
                  key={value}
                  content={supportsHover ? t(`tools.oneClickMv.scene.${value}.description`) : null}
                  contentClassName="max-w-[240px] px-3 py-2 text-xs leading-[1.4]"
                  side="top"
                  sideOffset={8}
                  trigger="hover"
                >
                  <button
                    type="button"
                    aria-pressed={isActive}
                    className={cn(
                      'flex h-12 min-w-0 items-center justify-center gap-2 rounded-8 border px-2 transition-colors',
                      isActive
                        ? 'border-[#5765F2] bg-[#5765F2]/15 text-system-white'
                        : 'border-system-white08 bg-system-white04 text-system-white/70 hover:border-system-white/20 hover:bg-system-white08 hover:text-system-white',
                    )}
                    onClick={() => onSceneChange(value)}
                  >
                    <span
                      className={cn(
                        'flex size-7 shrink-0 items-center justify-center rounded-6',
                        isActive ? 'bg-[#5765F2] text-white' : 'bg-system-white08 text-system-white/60',
                      )}
                    >
                      <Icon className="size-4" />
                    </span>
                    <span className="truncate font-medium text-sm">{t(`tools.oneClickMv.scene.${value}.label`)}</span>
                  </button>
                </CustomTooltip>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};
